import React, { useState } from 'react';
import Button from '../common/Button';
import FormField from '../common/FormField';
import { DedicatedStatusQuestionConstants } from '../../constants/DedicatedStatusQuestionConstants';

interface DedicatedStatusQuestionsProps {
  answers?: Record<string, boolean>;
  onAnswersChange?: (answers: Record<string, boolean>) => void;
  onClose?: () => void;
  onComplete?: () => void;
  isHuntingCourse?: boolean;
}

const DedicatedStatusQuestions: React.FC<DedicatedStatusQuestionsProps> = ({
  answers = {},
  onAnswersChange,
  onClose,
  onComplete,
  isHuntingCourse = true
}) => {
  const [formData, setFormData] = useState<Record<string, boolean>>(answers);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const { id, checked } = target;
    const newAnswers = { ...formData, [id]: checked };
    setFormData(newAnswers);

    // Clear error when checkbox is checked
    if (checked && errors[id]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }

    if (onAnswersChange) {
      onAnswersChange(newAnswers);
    }
  };

  const validateForm = (): boolean => {
    const formErrors: { [key: string]: string } = {};

    // Build required questions based on course type
    const question2Key = isHuntingCourse ? 'question2_hunting' : 'question2_sport';
    const requiredQuestions = ['question1', question2Key, 'question3'];

    // Question D (question4) is only for hunting courses
    if (isHuntingCourse) {
      requiredQuestions.push('question4');
    }

    requiredQuestions.forEach(questionKey => {
      if (!formData[questionKey]) {
        formErrors[questionKey] = 'This question must be confirmed to proceed.';
      }
    });

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleComplete = () => {
    if (validateForm() && onComplete) {
      onComplete();
    }
  };
  const renderQuestionItem = (questionKey: string, letter: string) => {
    const question = DedicatedStatusQuestionConstants[questionKey];
    if (!question) return null;

    return (
      <FormField
        key={questionKey}
        id={questionKey}
        label={`Question ${letter}`}
        type="checkbox"
        subtext={question.questionText}
        value={formData[questionKey] || false}
        onChange={handleChange}
        helper="I Confirm?"
        error={errors[questionKey]}
        required={true}
      />
    );
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    }
  };

  // Build questions array based on course type
  const questions = [
    { key: 'question1', letter: 'A' },
    { key: isHuntingCourse ? 'question2_hunting' : 'question2_sport', letter: 'B' },
    { key: 'question3', letter: 'C' },
    ...(isHuntingCourse ? [{ key: 'question4', letter: 'D' }] : [])
  ];

  return (
    <div className="p-6 bg-white">
      <h2 className="text-lg font-semibold text-purple-700 mb-6">Application details</h2>

      <div className="space-y-4">
        {questions.map((question) =>
          renderQuestionItem(question.key, question.letter)
        )}
      </div>

      <div className="flex justify-end space-x-4 mt-8">
        <Button variant="secondary" onClick={handleBack}>
          <span className="mr-2">←</span> Back
        </Button>
        <Button
          variant="primary"
          onClick={handleComplete}
        >
          Complete
        </Button>
      </div>
    </div>
  );
};

export default DedicatedStatusQuestions;